// frontend/src/admin/pages/AdminTicketDetailPage.jsx
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  addAdminTicketMessage,
  getAdminTicket,
  updateAdminTicket,
} from "../api";

const STATUSES = ["open", "in_progress", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const AUTHOR_LABEL = { user: "User", ai: "AI Solution", operator: "Operator" };

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function Meta({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-700">{value ?? "—"}</dd>
    </div>
  );
}

/**
 * AdminTicketDetailPage — full operator view of one ticket: metadata, the
 * conversation thread (question → AI solution → replies), and actions (reply,
 * change status/priority) (M4a-A7).
 */
function AdminTicketDetailPage() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const data = await getAdminTicket(id);
    setTicket(data);
  };

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch {
        toast.error("Failed to load ticket.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addAdminTicketMessage(id, reply.trim());
      setReply("");
      await load();
      toast.success("Reply sent.");
    } catch {
      toast.error("Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  const onPatch = async (patch) => {
    try {
      const updated = await updateAdminTicket(id, patch);
      setTicket(updated);
      toast.success("Ticket updated.");
    } catch {
      toast.error("Failed to update ticket.");
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!ticket) return <p className="text-sm text-slate-500">Ticket not found.</p>;

  return (
    <div className="max-w-3xl">
      <Link to="/admin/tickets" className="text-sm text-indigo-600 hover:underline">
        ← Back to tickets
      </Link>

      <h1 className="text-xl font-semibold text-slate-900 mt-3 mb-1">
        {ticket.subject}
      </h1>
      {ticket.sla_breached && (
        <p className="text-xs font-semibold text-rose-600 mb-3">
          ⚠️ SLA breached
        </p>
      )}

      {/* Metadata */}
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white border border-slate-200 rounded-xl p-4 mb-5">
        <Meta label="Org" value={ticket.org_name} />
        <Meta label="User" value={ticket.user_email} />
        <Meta label="Tier" value={ticket.plan_tier} />
        <Meta label="Category" value={ticket.category} />
        <Meta label="Created by" value={ticket.agent_name} />
        <Meta label="Created" value={fmt(ticket.created_at)} />
        <Meta label="AI responded" value={fmt(ticket.ai_responded_at)} />
        <Meta label="Human responded" value={fmt(ticket.first_human_response_at)} />
        <Meta label="SLA due" value={fmt(ticket.sla_due_at)} />
        <Meta label="Resolved" value={fmt(ticket.resolved_at)} />
        <Meta
          label="Time to resolution"
          value={
            ticket.time_to_resolution_seconds != null
              ? `${Math.round(ticket.time_to_resolution_seconds / 3600)}h`
              : "—"
          }
        />
      </dl>

      {/* Status / priority controls */}
      <div className="flex flex-wrap gap-4 mb-5">
        <label className="block">
          <span className="block text-xs font-medium text-slate-500 mb-1">Status</span>
          <select
            aria-label="Set status"
            value={ticket.status}
            onChange={(e) => onPatch({ status: e.target.value })}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-slate-500 mb-1">Priority</span>
          <select
            aria-label="Set priority"
            value={ticket.priority}
            onChange={(e) => onPatch({ priority: e.target.value })}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm"
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Conversation */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-slate-700">Conversation</h2>
        <div className="rounded-lg bg-slate-50 p-3">
          <div className="text-[11px] font-semibold text-slate-400 mb-0.5">
            User — question
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">
            {ticket.description}
          </p>
        </div>
        {(ticket.messages || []).map((m) => (
          <div key={m.id} className="rounded-lg bg-slate-50 p-3">
            <div className="text-[11px] font-semibold text-slate-400 mb-0.5">
              {AUTHOR_LABEL[m.author_type] || m.author_type} · {fmt(m.created_at)}
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}

        <form onSubmit={onReply} className="pt-2 border-t border-slate-100 space-y-2">
          <textarea
            aria-label="Operator reply"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            placeholder="Reply as operator…"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send reply"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminTicketDetailPage;
